import {http} from '@teemill/common/services';
import ruleSet from './validator/rules';
import {merge} from 'lodash';

const defaultConfig = {
  // if fail silently is set to true then validation errors will still be shown to the user
  // but the form won't be considered to be in a failing state (validation will pass)
  failSilently: false,
};

export const teeValidator = {
  ruleSet,
  fields: {},
  /**
   * Link a field name to some validation rules
   *
   * @param {String} fieldName The name of the field
   * @param {Object} validationRules The rules to apply to this field.
   *  These are in the format { ruleName => ruleValue, ... }
   */
  attach(fieldName, validationRules) {
    if (validationRules) {
      if (!validationRules.config) {
        validationRules.config = defaultConfig;
      } else {
        validationRules.config = merge(
          {},
          defaultConfig,
          validationRules.config
        );
      }
    }

    this.fields[fieldName] = validationRules;
  },

  detach(fieldName) {
    delete this.fields[fieldName];
  },
  /**
   * Validate a field
   *
   * @param {String} fieldName The name of the field
   * @param {*} value The current value of the field
   *
   * @return {Promise} a Promise that resolves if the field is valid or rejects if it is not.
   *  The promise will contain an array of errors
   */
  validate(fieldName, value, inclAjax) {
    return new Promise((resolve, reject) => {
      this.validateField(fieldName, value, inclAjax).then(
        ({errors, advisory = false}) => {
          if (errors && errors.length) {
            reject({errors, advisory});
          }

          resolve(true);
        }
      );
    });
  },
  /**
   * Used internally to validate a field within a Promise. This should not be called externally.
   */
  validateField(fieldName, value, inclAjax) {
    return new Promise(resolve => {
      const errors = [];
      let tests = 0;
      let completedTests = -1; // must be minus one until all rules are prepped
      let allRulesPrepped = false;
      const rules = this.fields[fieldName];

      function afterRuleValidated() {
        completedTests += 1;
        if (allRulesPrepped && tests === completedTests) {
          if (!rules.config.failSilently) {
            resolve({errors});
          } else {
            resolve({errors, advisory: true});
          }
        }
      }
      // eslint-disable-next-line
      if (!rules) {
        resolve({errors});
        return;
      }
      Object.keys(rules).forEach(rule => {
        if (
          typeof this.ruleSet[rule] === 'undefined' &&
          typeof rules[rule] === 'object' &&
          rules[rule].test instanceof Function
        ) {
          this.ruleSet[rule] = rules[rule];
          rules[rule] = true;
        }

        if (
          rules[rule] !== false &&
          typeof this.ruleSet[rule] !== 'undefined'
        ) {
          tests += 1;
          const ruleSetObj = this.ruleSet[rule];
          if (ruleSetObj.regex && rules[rule]) {
            const isEmptyValue =
              typeof value === 'string' ? value.length === 0 : !value;
            if (!(ruleSetObj.allowEmpty && (isEmptyValue || value === null))) {
              if (
                typeof ruleSetObj.allowEmpty !== 'undefined' &&
                !ruleSetObj.allowEmpty &&
                value === null
              ) {
                errors.push(
                  this.getErrorMessage(ruleSetObj, rules[rule], fieldName)
                );
              }
              let regexResult = this.regexTest(ruleSetObj.regex, value);
              regexResult = ruleSetObj.ifNotMatch ? regexResult : !regexResult;
              if (regexResult) {
                errors.push(
                  this.getErrorMessage(ruleSetObj, rules[rule], fieldName)
                );
              }
            }
            afterRuleValidated();
          } else if (typeof ruleSetObj.test === 'function') {
            // function test
            let testResult = ruleSetObj.test(rules[rule], value);

            if (!(testResult instanceof Promise)) {
              testResult = new Promise(r => r(testResult));
            }

            testResult.then(result => {
              result = ruleSetObj.ifNotMatch ? result : !result;
              if (result) {
                errors.push(
                  this.getErrorMessage(ruleSetObj, rules[rule], fieldName)
                );
              }
              afterRuleValidated();
            });
          } else if (typeof ruleSetObj.ajax === 'string') {
            // ajax test
            // don't bother sending off the ajax request if we already have errors
            if (inclAjax && !errors.length) {
              const postVals = Object.assign({}, ruleSetObj.ajaxParams || {}, {
                value,
              });
              http.post(ruleSetObj.ajax, postVals).then(response => {
                if (!response.data) {
                  errors.push(
                    this.getErrorMessage(
                      ruleSetObj,
                      rules[rule],
                      fieldName,
                      value
                    )
                  );
                }
                afterRuleValidated();
              });
            } else {
              afterRuleValidated();
            }
          }
        }
      });
      allRulesPrepped = true;
      afterRuleValidated();
    });
  },
  /**
   * Perform a rule's regex test on a value
   *
   * @param {Regex} regex The regex pattern to match
   * @param {String} value The string to test
   *
   * @return {Boolean} True if valid, false if invalid.
   */
  regexTest(regex, value) {
    if (value === null || typeof value === 'undefined') {
      return false;
    }
    const matches = String(value).match(regex);
    return matches && matches.length;
  },
  /**
   * Returns the error message of a rule
   *
   * @param {Object} ruleSetObj The object from this.ruleSet for this rule
   * @param {Object} rule The rule provided by the field's validationRules
   * @param {String} fieldName The name of the field
   *
   * @return {String} The error message
   */
  getErrorMessage(ruleSetObj, rule, fieldName, fieldValue) {
    if (typeof ruleSetObj.message === 'function') {
      return ruleSetObj.message(rule, fieldName, fieldValue);
    }
    return ruleSetObj.message;
  },
  /**
     * Define a custom rule.
     *
     * @param {String} name The name of the rule.
     *  If a rule already exists with this name, it will be replaced.
     * @param {Object} options The rule object.
     * @example TeeValidate.validator.addCustomRule('ruleName', {
        regex: /^pattern$/,
        test(ruleValue, fieldValue) {
          // if regex isn't applicable you can use a 'test' function instead
          // This function should return false if invalid, true if valid.
          return fieldValue >= ruleValue;
        },
        message(ruleValue, fieldName) {
          return `Message can be a function with properties ${fieldName} and ${ruleValue}`
        },
        message: 'Message can alternatively be a fixed string',
      });
     */
  addCustomRule(name, options) {
    this.ruleSet[name] = options;
  },
};
